import { authFetch } from "@/lib/api-client";

const BASE_PATH = "/admin/bigquery-usage";

/**
 * The three categories a row's tenant can fall into, distinct all the way to
 * the screen. `platform` is work the platform did for itself; `unattributed` is
 * work whose caller was never recorded. Collapsing them would make the
 * unattributed KPI impossible to drill into.
 */
export const BIGQUERY_USAGE_TENANT_KINDS = ["tenant", "platform", "unattributed"] as const;
export type BigQueryUsageTenantKind = (typeof BIGQUERY_USAGE_TENANT_KINDS)[number];

export const BIGQUERY_USAGE_EXECUTION_SORT_COLUMNS = [
  "createdAt",
  "bytesBilled",
  "durationMs",
] as const;
export type BigQueryUsageExecutionSortBy =
  (typeof BIGQUERY_USAGE_EXECUTION_SORT_COLUMNS)[number];
export type BigQueryUsageSortOrder = "asc" | "desc";

/** Whether this deployment persists BigQuery telemetry at all. */
export type BigQueryTelemetryMode = "recording" | "not-recording";

/**
 * The shared narrowing every read but `/monitoring` accepts.
 *
 * `windowHours` must be one of the offered rolling windows — the API rejects
 * anything else with a 400 rather than substituting a default, because a
 * substituted window reports money for a period nobody asked about. Passing
 * `tenantKind: "tenant"` without a `tenantId` is likewise a 400.
 */
export interface BigQueryUsageFilters {
  windowHours?: number;
  feature?: string;
  tenantKind?: BigQueryUsageTenantKind;
  tenantId?: string;
  sqlFingerprint?: string;
  cacheHit?: boolean;
  /**
   * Free text matched against the statement, the BigQuery job id and the
   * correlation id. Substring rather than exact, because the executions table
   * truncates job ids and an admin will paste part of one.
   */
  search?: string;
  /** ISO bounds from a chart-bucket narrowing, which override the window. */
  since?: string;
  until?: string;
}

export interface BigQueryUsageGroupTotals {
  queries: number;
  bytesBilled: number;
  bytesProcessed: number;
  slotMs: number;
  /** A load signal, never money — the project has no slot reservation. */
  slotHours: number;
  cacheHits: number;
  cacheHitRate: number | null;
  avgDurationMs: number | null;
  /**
   * Micro-dollars, or null when no price per TB is configured. Null is a state
   * the UI renders as unavailable — never coerce it to zero, which would read
   * as a priced `$0.00`.
   */
  costMicros: number | null;
}

/**
 * Enough tenant identity to draw the same logo mark the rest of the app draws.
 * `logoUrl` arrives ready for an `<img>` — a private object is signed by the
 * API, so no upload id reaches the client. Both are null for the two special
 * buckets, which have no branding of their own, and for a tenant whose row is
 * gone.
 */
export interface BigQueryUsageTenantBranding {
  tenantName: string | null;
  brandColor: string | null;
  logoUrl: string | null;
}

/**
 * Who a row belongs to, with no branding attached. The timeseries sends this
 * shape and only this shape: a band is drawn in its series' own color, so
 * signing a logo once per bucket per tenant would buy nothing.
 */
export interface BigQueryUsageTenantIdentity {
  kind: BigQueryUsageTenantKind;
  /** Null for both special buckets; `kind` says which one. */
  tenantId: string | null;
  tenantName: string | null;
}

export interface BigQueryUsageTenantRef
  extends BigQueryUsageTenantIdentity,
    BigQueryUsageTenantBranding {}

export interface BigQueryUsageFeatureTotals extends BigQueryUsageGroupTotals {
  feature: string;
}

export interface BigQueryUsageTenantTotals
  extends BigQueryUsageGroupTotals,
    BigQueryUsageTenantRef {}

export interface BigQueryUsageStats extends BigQueryUsageGroupTotals {
  windowHours: number;
  since: string;
  until: string;
  p95DurationMs: number | null;
  unattributedQueries: number;
  byFeature: BigQueryUsageFeatureTotals[];
  byTenant: BigQueryUsageTenantTotals[];
  /**
   * Separates R19's "nothing is recorded in this environment" from an idle
   * window, which a row count reads the same.
   */
  telemetryMode: BigQueryTelemetryMode;
}

interface BigQueryUsageBucketBand {
  queries: number;
  bytesBilled: number;
}

export interface BigQueryUsageBucketFeature extends BigQueryUsageBucketBand {
  feature: string;
}

export interface BigQueryUsageBucketTenant
  extends BigQueryUsageBucketBand,
    BigQueryUsageTenantIdentity {}

export interface BigQueryUsageBucketCacheState extends BigQueryUsageBucketBand {
  cacheHit: boolean;
}

export interface BigQueryUsageBucket {
  bucketStart: string;
  queries: number;
  bytesBilled: number;
  slotMs: number;
  cacheHits: number;
  costMicros: number | null;
  /** Null for a bucket nothing landed in, which the chart draws as a gap. */
  p95DurationMs: number | null;
  features: BigQueryUsageBucketFeature[];
  tenants: BigQueryUsageBucketTenant[];
  cacheStates: BigQueryUsageBucketCacheState[];
}

export interface BigQueryUsageTimeseries {
  windowHours: number;
  bucketMinutes: number;
  since: string;
  until: string;
  /** Gapless and ascending: a quiet hour is a zero, never a missing bar. */
  buckets: BigQueryUsageBucket[];
}

export interface BigQueryUsageFingerprint extends BigQueryUsageGroupTotals {
  sqlFingerprint: string;
  /** The normalized template, never a truncation of an executed statement. */
  sqlPreview: string;
  fingerprintVersion: number;
  lastSeenAt: string;
}

export interface BigQueryUsageExecution extends BigQueryUsageTenantBranding {
  id: string;
  createdAt: string;
  feature: string;
  tenantId: string | null;
  tenantKind: BigQueryUsageTenantKind;
  correlationId: string | null;
  bytesBilled: number;
  bytesProcessed: number;
  slotMs: number;
  /** This execution's bytes at the configured rate. Null when no rate is set. */
  costMicros: number | null;
  cacheHit: boolean;
  jobId: string | null;
  durationMs: number;
  attempts: number;
  sqlFingerprint: string;
  sqlPreview: string;
  fingerprintVersion: number;
  errorReason: string | null;
}

export interface BigQueryUsageExecutionsPage {
  items: BigQueryUsageExecution[];
  nextCursor?: string;
  hasMore: boolean;
  /** Answered on the uncursored first page only, so paging cannot re-count. */
  totalCount?: number;
}

/** The executions list narrows by the shared filter plus its own sort and size. */
export interface BigQueryUsageExecutionsFilters extends BigQueryUsageFilters {
  sortBy?: BigQueryUsageExecutionSortBy;
  sortOrder?: BigQueryUsageSortOrder;
  limit?: number;
}

export interface BigQueryUsageFeatureOption {
  feature: string;
  /** Null for a feature the service registry does not cover. */
  service: string | null;
}

export interface BigQueryUsageMonitoringBucket {
  startTime: string;
  queries: number;
  bytesBilled: number;
}

/**
 * What Google itself accounted for over the same window.
 *
 * `available: false` means Cloud Monitoring could not be read at all, which
 * arrives as a 200 because the panel is supplementary. The two metrics carry
 * their own flags: one of them answering is worth showing, and the other side's
 * total is then a structural zero rather than a measurement.
 */
export interface BigQueryUsageMonitoring {
  available: boolean;
  windowHours: number;
  bucketMinutes: number;
  since: string;
  until: string;
  queriesAvailable: boolean;
  bytesBilledAvailable: boolean;
  totalQueries: number;
  totalBytesBilled: number;
  /** Sparse and ascending — Monitoring reports only the buckets it has. */
  buckets: BigQueryUsageMonitoringBucket[];
}

function filterParams(filters: BigQueryUsageFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.windowHours !== undefined) params.set("windowHours", String(filters.windowHours));
  if (filters.feature) params.set("feature", filters.feature);
  if (filters.tenantKind) params.set("tenantKind", filters.tenantKind);
  if (filters.tenantId) params.set("tenantId", filters.tenantId);
  if (filters.sqlFingerprint) params.set("sqlFingerprint", filters.sqlFingerprint);
  // Query strings carry no booleans, and `cacheHit=false` must not read as
  // absent — it is the "only cache misses" narrowing.
  if (filters.cacheHit !== undefined) params.set("cacheHit", String(filters.cacheHit));
  if (filters.search) params.set("search", filters.search);
  if (filters.since) params.set("since", filters.since);
  if (filters.until) params.set("until", filters.until);
  return params;
}

function toQuery(params: URLSearchParams): string {
  const query = params.toString();
  return query ? `?${query}` : "";
}

function windowQuery(windowHours?: number): string {
  return windowHours === undefined ? "" : `?windowHours=${windowHours}`;
}

export async function fetchBigQueryUsageStats(
  filters: BigQueryUsageFilters,
): Promise<BigQueryUsageStats> {
  const { data } = await authFetch<BigQueryUsageStats>(
    `${BASE_PATH}/stats${toQuery(filterParams(filters))}`,
  );
  return data;
}

export async function fetchBigQueryUsageTimeseries(
  filters: BigQueryUsageFilters,
): Promise<BigQueryUsageTimeseries> {
  const { data } = await authFetch<BigQueryUsageTimeseries>(
    `${BASE_PATH}/timeseries${toQuery(filterParams(filters))}`,
  );
  return data;
}

export async function fetchBigQueryUsageFingerprints(
  filters: BigQueryUsageFilters,
  limit?: number,
): Promise<BigQueryUsageFingerprint[]> {
  const params = filterParams(filters);
  if (limit !== undefined) params.set("limit", String(limit));
  const { data } = await authFetch<BigQueryUsageFingerprint[]>(
    `${BASE_PATH}/fingerprints${toQuery(params)}`,
  );
  return data;
}

export async function fetchBigQueryUsageExecutions(
  filters: BigQueryUsageExecutionsFilters,
  cursor?: string,
): Promise<BigQueryUsageExecutionsPage> {
  const params = filterParams(filters);
  if (filters.sortBy) params.set("sortBy", filters.sortBy);
  if (filters.sortOrder) params.set("sortOrder", filters.sortOrder);
  if (filters.limit !== undefined) params.set("limit", String(filters.limit));
  if (cursor) params.set("cursor", cursor);
  const { data } = await authFetch<BigQueryUsageExecutionsPage>(
    `${BASE_PATH}/executions${toQuery(params)}`,
  );
  return data;
}

/**
 * The window's features with the service that owns each. The mapping comes from
 * the API rather than a bundled copy, so a stale bundle cannot drop a feature's
 * bytes out of the service rollup.
 */
export async function fetchBigQueryUsageFeatures(
  windowHours?: number,
): Promise<BigQueryUsageFeatureOption[]> {
  const { data } = await authFetch<{ features: BigQueryUsageFeatureOption[] }>(
    `${BASE_PATH}/features${windowQuery(windowHours)}`,
  );
  return data.features;
}

export async function fetchBigQueryUsageTenants(
  windowHours?: number,
): Promise<BigQueryUsageTenantRef[]> {
  const { data } = await authFetch<BigQueryUsageTenantRef[]>(
    `${BASE_PATH}/tenants${windowQuery(windowHours)}`,
  );
  return data;
}

/**
 * Takes the window and nothing else: Cloud Monitoring accounts for the project,
 * so a filter here would narrow one side of the comparison and not the other.
 */
export async function fetchBigQueryUsageMonitoring(
  windowHours?: number,
): Promise<BigQueryUsageMonitoring> {
  const { data } = await authFetch<BigQueryUsageMonitoring>(
    `${BASE_PATH}/monitoring${windowQuery(windowHours)}`,
  );
  return data;
}
